import { ComponentProps, RefObject, useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { ParsedPointId } from "@fidelix/fx-margaret";
import {
  LinkButton,
  Button,
  Icon,
  IconButton,
  Input,
  Popover,
  Select,
  Spinner,
  Stack,
  Text,
  theme,
  useParsedSearchParams,
} from "@fidelix/fx-miranda";
import { useForm } from "react-hook-form";
import {
  AILimitExtension,
  ControlType,
  LockState,
  ModeType,
  Point,
  PointType,
  PointSetValue,
} from "~point/point.model";
import { useUpdatePointMutation } from "~point/point.mutations";
import { useNavigatonPath, useRequiredParams } from "~utils/url";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { serializeHistoryParams } from "~utils/parse";
import { useWindowResizeEvent } from "~utils/observe";
import { getPointLockStateLabel } from "~point/point.utils";
import { parsePointName } from "../graphics/graphics.utils";
import { UpdateGraphicPointParams } from "./graphics.mutation";
import { useRealtimePointUpdatesContext } from "../contexts/realtime-point-values-context";
import { HasPermission } from "../utils/permission-helper";
import { DeviceCapabilities } from "../device/device.model";
import { hasDeviceCapability } from "../device/device.utils";
import { DeviceDisconnectedTooltip } from "../device/device-disconnected-tooltip";
import { useDeviceStateContext } from "../device/device-status-context";
import { Permissions } from "../const";
import { getControlPointValue } from "./control-point.utils";
import AlarmStatusBadge from "../alarm/alarm-status-badge";
import {
  getAlarmStateBadgeColor,
  getAlarmStateText,
} from "../alarm/alarm.utils";
import { usePointQuery } from "../point/point.queries";
import { Alarm } from "../alarm/alarm.model";
import { ViewGroupHistoryButton } from "../point/point-history/view-group-history-button";
import { AcknowledgeAlarmModal } from "../alarm/alarm-details/alarm-acknowledge-modal";
import {
  useGraphicsAttachmentsQuery,
  useGraphicsQuery,
} from "./graphics.queries";
import { PopoverHeader } from "./edit-point-popover";
import { PointWithGraphicMetaData } from "./graphics-context";

export type EditMode = "tooltip" | "edit" | "editWithoutViewButton";
type Placement = ComponentProps<typeof Popover>["placement"];

type IProps = {
  point: PointWithGraphicMetaData;
  onClose: () => void;
  triggerRef: RefObject<HTMLElement | null>;
  editMode: EditMode;
  placement?: Placement;
};

type IPointForm = {
  value: number | undefined;
  mode: string | undefined;
};

export function SetValuePopover(props: IProps) {
  const { onClose, editMode, point, placement } = props;
  const { innerWidth } = useWindowResizeEvent();
  const isPhone = innerWidth < theme.breakpoints.tablet.min;
  const popoverRef = useRef<HTMLDivElement>(null);
  const { data: pointWithValue, isLoading } = usePointQuery(
    {
      id: point?.id,
      deviceId: point?.device?.id,
    },
    {
      enabled: !!point,
      customQueryKey: "graphics-set-value-popover",
    },
  );

  if ((!isLoading && !pointWithValue) || !point) {
    onClose();
    alert(`${point?.name} not found`);
    return null;
  }

  return (
    <CustomPopover
      ref={popoverRef}
      triggerRef={props.triggerRef}
      isOpen={true}
      isNonModal
      onOpenChange={(isOpen) => {
        const active = document.activeElement;
        // Android scrolls the focused input into view when the keyboard opens, which closes non-modal popovers on scroll
        if (
          !isOpen &&
          active instanceof HTMLInputElement &&
          popoverRef.current?.contains(active)
        ) {
          return;
        }
        props.onClose();
      }}
      placement={isPhone ? "top" : placement || "bottom"}
      content={
        <PopoverContent
          {...props}
          point={point}
          pointWithValue={pointWithValue}
          isLoading={isLoading}
          editMode={editMode}
        />
      }
    />
  );
}

const mapLimitValuesToPoint = (
  point: Point,
  parsePoint: ParsedPointId,
  pointName: string,
  limits?: AILimitExtension[],
): Point => {
  const { limit } = parsePoint;
  const index = limit!;
  const limitData = limits && limits[index - 1];
  return {
    ...point,
    name: pointName,
    value: {
      ...point.value!,
      value: limitData?.value ?? null,
      lockState: limitData?.lockState ?? null,
    },
    stateTexts: [],
  };
};

const mapControlValuesToPoint = (
  point: Point,
  parsePoint: ParsedPointId,
  control?: ControlType,
): ParsedPointId & Point => {
  const { paramId } = parsePoint;
  const controlValue = control && control[paramId!];
  return {
    ...point,
    value: {
      ...point.value!,
      value: controlValue,
    },
  };
};

function mapValuesToPoint(
  parsePoint: ParsedPointId,
  point: Point,
  pointName: string,
): Point {
  const { limit, paramId } = parsePoint;
  if (limit) {
    return mapLimitValuesToPoint(
      point,
      parsePoint,
      pointName,
      point.value?.extension?.limits,
    );
  }

  if (paramId) {
    return mapControlValuesToPoint(
      point,
      parsePoint,
      point.value?.extension?.control,
    );
  }

  if (!parsePoint.fdxshowsetvalue || !point.value?.extension?.state) {
    return point;
  }

  return +parsePoint.fdxshowsetvalue
    ? point
    : {
        ...point,
        value: {
          ...point.value!,
          value: point.value.extension.state.value ?? null,
          lockState: point.value.extension.state.lockState ?? null,
        },
      };
}

const getAILimitPoint = (
  point: ParsedPointId & Point,
  data: IPointForm,
): PointSetValue => {
  return {
    id: point.id,
    name: point.name,
    type: point.type,
    value: {
      extension: {
        limits: [
          {
            index: point.limit!,
            value: data.mode === ModeType.Manual ? (data.value ?? 0) : null,
            lockState: data.mode ? LockState[data.mode] : undefined,
          },
        ],
      },
    },
  };
};

const getPopoverSubText = (
  point: ParsedPointId & Point,
  editMode: EditMode,
  isDeviceDisconnected: boolean,
  pointWithValue?: Point | Alarm,
  onAcknowledge?: () => void,
) => {
  if (!pointWithValue) {
    return;
  }
  if (point.paramId && pointWithValue.value?.extension?.control) {
    return (
      <Text variant="bodySmall">{getControlPointValue(point.paramId)}</Text>
    );
  }
  if (point.limit) {
    const limit = pointWithValue.value?.extension?.limits?.find(
      (l) => l.index === point.limit,
    );
    return <Text variant="bodySmall">{limit?.name || "-"}</Text>;
  }
  if (point.type === PointType.Alarm) {
    const alarm = pointWithValue as Alarm;
    const isUnacknowledged = alarm?.value?.isAcknowledged === false;
    return (
      <Stack axis="x" spacing="xsmall" align="center">
        <AlarmStatusBadge variant={getAlarmStateBadgeColor(alarm)} />
        <Text variant="bodySmall">{getAlarmStateText(alarm)}</Text>
        {isUnacknowledged && editMode === "edit" && (
          <>
            <HasPermission permission={Permissions.ALARM_EDIT}>
              <DeviceDisconnectedTooltip isDisconnected={isDeviceDisconnected}>
                <Button
                  variant="textUnderline"
                  onPress={onAcknowledge}
                  isDisabled={isDeviceDisconnected}
                >
                  <Trans>Acknowledge</Trans>
                </Button>
              </DeviceDisconnectedTooltip>
            </HasPermission>
          </>
        )}
      </Stack>
    );
  }
};

const PopoverContent = ({
  onClose,
  point,
  pointWithValue,
  isLoading,
  editMode,
}: {
  point: PointWithGraphicMetaData;
  pointWithValue?: Point;
  isLoading: boolean;
  onClose: () => void;
  editMode: EditMode;
}) => {
  const { to } = useNavigatonPath();
  const navigateTo =
    point && point.type === PointType.Alarm ? "alarms" : "points";
  const { levelId } = useParams();
  const { parsedParams } = useParsedSearchParams({
    graphic: { type: "string" },
    graphicsId: { type: "number" },
  });
  const { graphicsId, graphic } = parsedParams;

  const { getPoint } = useRealtimePointUpdatesContext();
  const pointModel = getPoint(point) || pointWithValue;
  const [showAcknowledgeModal, setShowAcknowledgeModal] = useState(false);
  const isDeviceDisconnected = useDeviceStateContext().isDeviceDisconnected(
    point.device.id,
  );
  const queryClient = useQueryClient();
  const { companyId } = useRequiredParams({
    companyId: "number",
  });

  const { data: graphics } = useGraphicsQuery(graphicsId!, {
    enabled: !!graphicsId,
    staleTime: Infinity,
  });

  const { data: attachments } = useGraphicsAttachmentsQuery(
    parsedParams.graphicsId!,
    {
      enabled: !!parsedParams.graphicsId,
      staleTime: Infinity,
    },
  );

  const attachment = attachments?.find(
    (a) => a.name.toLowerCase() === graphic?.toLowerCase(),
  );

  const limits = {};
  const { limit } = parsePointName(point.name);
  if (limit !== undefined) {
    for (let i = 1; i < 9; i++) {
      const limitId = point.id + ":" + i;
      limits[limitId] = Number(i === Number(limit));
    }
  }

  const getRoute = (point: Point) => {
    const selection = serializeHistoryParams({
      points: {
        [point.id]: {
          visible: limit !== undefined ? 0 : 1,
          showLimits: limit !== undefined ? 1 : 0,
        },
      },
      groups: {},
      limits: limits,
    });
    let defaultRouteParam = `?tab=history&selected=${selection}&timeRange=7d`;
    switch (point.type) {
      case PointType.ConversionTable:
        defaultRouteParam = `?tab=convTable`;
        break;
      case PointType.TimeSchedule:
        defaultRouteParam = `?tab=defaultSchedule`;
        break;
      case PointType.Alarm:
        defaultRouteParam = "";
        break;
    }
    return `devices/${point?.device.id}/${navigateTo}/${point?.id}${defaultRouteParam}`;
  };

  // Prevent editing control points with paramId defined until Cordelia supports it
  // Enable editing for control point parameter 210 only
  const paramId = point!.paramId ? parseInt(point!.paramId, 10) : undefined;
  const canEdit =
    hasDeviceCapability(point.device.type, DeviceCapabilities.EditValue) &&
    editMode !== "tooltip" &&
    (!paramId || (point!.type === PointType.Control && paramId === 210));

  // Check if point is an alarm
  const isAlarm = point.type === PointType.Alarm;
  const alarm = pointModel as Alarm;

  const handleAcknowledgeModalClose = () => {
    setShowAcknowledgeModal(false);
    // Invalidate graphics popover query to refresh the alarm status
    queryClient.invalidateQueries({
      queryKey: ["graphics-set-value-popover"],
    });
  };

  const isScadaGraphic = graphics?.device === null;
  const scadaEditPointParams: UpdateGraphicPointParams | undefined =
    isScadaGraphic
      ? {
          companyId,
          graphicsId: graphicsId!,
          graphic: attachment?.name,
          graphicNodeId: point.graphicNodeId,
          pointName: point.name,
          deviceId: point.device.externalId,
        }
      : undefined;

  return (
    <Stack axis="y" spacing="small">
      {showAcknowledgeModal && isAlarm && alarm && (
        <AcknowledgeAlarmModal
          alarm={alarm}
          onClose={handleAcknowledgeModalClose}
        />
      )}
      <PointDetailContainer axis="y" spacing="small">
        <PopoverHeader
          editMode={editMode === "edit"}
          name={point.name}
          clipBoardContent={
            levelId ? `${point?.device.externalId}.${point.name}` : point.name
          }
          scadaEditPointParams={scadaEditPointParams}
          onClose={onClose}
          errorState={pointModel?.value?.errorState}
        />
        <Text variant="bodySmall">{point.description}</Text>
        {getPopoverSubText(
          point,
          editMode,
          isDeviceDisconnected,
          pointModel,
          () => setShowAcknowledgeModal(true),
        )}
        {point.type !== PointType.ConversionTable && pointModel && (
          <EditPointValue
            editMode={editMode}
            enableEdit={canEdit}
            isDeviceDisconnected={isDeviceDisconnected}
            point={point}
            pointWithValue={pointModel}
            onClose={onClose}
            isLoading={isLoading}
          />
        )}
      </PointDetailContainer>
      <HasPermission permission={Permissions.POINTS_VIEW}>
        {editMode === "edit" && pointWithValue && (
          <ViewPointContainer>
            <LinkButton
              size="small"
              variant="textUnderline"
              icon="chevronRight"
              data-test-id="view-point-button"
              to={to(getRoute(pointWithValue))}
            >
              <Trans>View point</Trans>
            </LinkButton>
            <ViewGroupHistoryButton
              pointGroups={pointWithValue.pointGroups}
              variant="text"
            >
              <Trans>View group</Trans>
            </ViewGroupHistoryButton>
          </ViewPointContainer>
        )}
      </HasPermission>
    </Stack>
  );
};

const EditPointValue = ({
  point,
  onClose,
  isLoading,
  enableEdit,
  isDeviceDisconnected,
  editMode,
  pointWithValue,
}: {
  point: ParsedPointId & Point;
  onClose: () => void;
  isLoading: boolean;
  enableEdit: boolean;
  isDeviceDisconnected: boolean;
  editMode: EditMode;
  pointWithValue: Point;
}) => {
  const [isEdit, setIsEdit] = useState(editMode === "editWithoutViewButton");
  const { getPoint } = useRealtimePointUpdatesContext();
  const mappedPoint = mapValuesToPoint(point, pointWithValue, point.name);

  const { register, handleSubmit, setValue, watch } = useForm<IPointForm>({
    defaultValues: {
      value: mappedPoint.value?.value ?? undefined,
      mode:
        mappedPoint.value?.lockState != null
          ? LockState[mappedPoint.value?.lockState]
          : undefined,
    },
    mode: "all",
  });

  useEffect(() => {
    if (!isEdit) return;
    const realTimePoint = getPoint(point);
    const basePoint = realTimePoint ?? pointWithValue;
    const latest = mapValuesToPoint(point, basePoint, point.name);
    const latestMode =
      latest.value?.lockState != null
        ? LockState[latest.value.lockState]
        : undefined;
    setValue("mode", latestMode, { shouldValidate: true });
    setValue("value", latest.value?.value ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  const updatePoint = useUpdatePointMutation();

  const onSubmit = async (data: IPointForm) => {
    const isAILimitPoint = PointType.AI === point.type && point.limit;

    if (!pointWithValue || !pointWithValue.value) {
      return;
    }

    const wholePoint: PointSetValue = isAILimitPoint
      ? getAILimitPoint(point, data)
      : {
          id: point.id,
          type: point.type,
          value: {
            value: data.value,
            lockState: data.mode ? LockState[data.mode] : undefined,
          },
        };
    await updatePoint.mutateAsync(wholePoint, {
      onSuccess: () => onClose(),
    });
  };

  const onSelect = (fieldName: "value" | "mode", value: string) => {
    setValue(
      "value",
      value === ModeType.Auto ? undefined : (selectedValue ?? 0),
      { shouldValidate: true },
    );
    setValue(fieldName, value, { shouldValidate: true });
  };

  const selectedMode = watch("mode");
  const selectedValue = watch("value");

  const getDisplayPointValue = () => {
    const { stateTexts, unit, value } = mappedPoint;
    const lockStateValue =
      value?.lockState != null ? getPointLockStateLabel(value.lockState) : "";

    if (value?.value == null) {
      return "";
    }

    if (stateTexts?.length) {
      const stateText = stateTexts.find(
        (stateText) => stateText.value === value?.value,
      );
      return `${stateText?.label ?? value.value} ${lockStateValue}`;
    }
    return `${value?.value} ${unit || ""} ${lockStateValue}`;
  };

  if (!isEdit) {
    return (
      <TextContainer variant="subtitle2Bold">
        <Trans>Value:</Trans>{" "}
        {isLoading ? <LoadingPlaceholder /> : getDisplayPointValue()}
        {enableEdit && (
          <HasPermission permission={Permissions.POINTSTATE_EDIT}>
            <DeviceDisconnectedTooltip isDisconnected={isDeviceDisconnected}>
              <Icon
                onClick={
                  isDeviceDisconnected ? undefined : () => setIsEdit(true)
                }
                icon="edit"
                size={20}
                cursor={isDeviceDisconnected ? "not-allowed" : "pointer"}
                color={isDeviceDisconnected ? "lightNeutral" : undefined}
              />
            </DeviceDisconnectedTooltip>
          </HasPermission>
        )}
      </TextContainer>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack axis="y" spacing="small" align="start">
        <Stack
          align="center"
          spacing="normal"
          style={{ paddingTop: theme.space("normal") }}
        >
          <InputContainer axis="y" spacing="xxsmall" width="120px">
            {mappedPoint?.stateTexts && mappedPoint?.stateTexts?.length > 0 ? (
              <>
                <Select
                  onSelect={(option) => onSelect("value", option)}
                  aria-labelledby="value"
                  labelPosition="overlay"
                  name="value"
                  label={t`Value`}
                  selected={selectedValue?.toString()}
                  {...register}
                  isDisabled={
                    selectedMode === ModeType.Auto ||
                    selectedMode === ModeType.Program
                  }
                >
                  {mappedPoint.stateTexts.map((option) => (
                    <Select.Option
                      key={option.value}
                      value={option.value.toString()}
                    >
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </>
            ) : (
              <Input
                {...register("value")}
                type="number"
                label={t`Value`}
                labelPosition="overlay"
                isDisabled={
                  selectedMode === ModeType.Auto ||
                  selectedMode === ModeType.Program
                }
                endAdornment={mappedPoint?.unit || ""}
                defaultValue={mappedPoint?.value?.value?.toString()}
                onChange={(value) => {
                  setValue("value", Number(value), {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
              />
            )}
          </InputContainer>
          <InputContainer axis="y" spacing="xxsmall" width="120px">
            <Select
              onSelect={(option) => onSelect("mode", option)}
              label={t`Mode`}
              labelPosition="overlay"
              aria-labelledby="mode"
              name="mode"
              selected={selectedMode}
              {...register}
            >
              {Object.values(ModeType).map((mode: string) => (
                <Select.Option
                  style={{
                    display: mode === ModeType.Program ? "none" : "block",
                  }}
                  key={mode}
                  value={mode}
                >
                  {getPointLockStateLabel(LockState[mode])}
                </Select.Option>
              ))}
            </Select>
          </InputContainer>
          <Stack justify="space-evenly" align="end">
            <IconButton
              variant="secondary"
              icon="close"
              onClick={() => {
                updatePoint.reset();
                setIsEdit(false);
              }}
              aria-label="Cancel edit value"
            />
            {updatePoint.isPending ? (
              <Spinner color="darkPrimary" size={31} />
            ) : (
              <IconButton
                icon="check"
                buttonType="submit"
                aria-label="Submit edit value"
                isDisabled={selectedMode === ModeType.Program}
              />
            )}
          </Stack>
        </Stack>
        {updatePoint.isError && (
          <Text variant="body" color="error">
            <Trans>Failed to edit the point.</Trans>
          </Text>
        )}
      </Stack>
    </form>
  );
};
export const CustomPopover = styled(Popover)`
  border-radius: 8px;
  border-color: ${(p) => p.theme.colors.lightNeutral};
  box-shadow: 4px 4px 4px ${(p) => p.theme.colors.black}26;
  max-width: 90vw;
`;

const TextContainer = styled(Text)`
  display: flex;
  margin-top: ${(p) => p.theme.space("small")};
  gap: ${(p) => p.theme.space("small")};
`;

const InputContainer = styled(Stack)<{ width: string }>`
  width: ${(props) => props.width};
  @media (max-width: ${(p) => p.theme.breakpoints.phone.max}px) {
    width: 100px;
  }
`;

const ViewPointContainer = styled(Stack)`
  padding: 0 ${(p) => p.theme.space("small")};
  padding-top: ${(p) => p.theme.space("small")};
  border-top: 1px solid ${(p) => p.theme.colors.lightNeutral};
`;

const PointDetailContainer = styled(Stack)`
  padding: ${(p) => p.theme.space("small")};
`;

const shimmerAnimation = keyframes`
 from {
    background-position: -500px 0;
  }
  to {
    background-position: 500px 0;
  }
`;

const shimmer = css`
  animation: ${shimmerAnimation} 1.7s forwards infinite linear;
  background-repeat: no-repeat;
  background-size: 1000px 100%;
  background-color: ${(p) => p.theme.colors.mutedLightNeutral};
  background-image: linear-gradient(
    to right,
    ${(p) => p.theme.colors.mutedLightNeutral} 0%,
    ${(p) => p.theme.colors.lightNeutral} 20%,
    ${(p) => p.theme.colors.mutedLightNeutral} 40%,
    ${(p) => p.theme.colors.mutedLightNeutral} 100%
  );
`;

const LoadingPlaceholder = styled.div`
  width: 100%;
  border-radius: 3px;
  height: ${(p) => p.theme.space("medium")};
  ${shimmer}
`;
